import React, { forwardRef, memo, useEffect, useMemo } from 'react';
import { Dropdown as AntDropdown } from 'antd';
import { useValueRef } from '../../hooks/useValueRef';
import {
  filterOptions,
  getFlattenOptionsAndCount,
  getOptionsFromChildren,
} from './utils';
import {
  HandlersContext,
  StoreContext,
  useHandlers,
  ValueContext,
} from './contexts';
import { useCreateHandlers } from './hooks/useCreateHandlers';
import Control from './components/Control/Control';
import ObservableStore from './observableStore';
import { getInitialState, stateKeys, updatedProps } from './model';
import { useStoreSelector } from './hooks/useStoreSelector';
import { selectors } from './model';
import OptionsContainer from './components/OptionsContainer/OptionsContainer';

/**
 * Компонент DropdownSelect
 *
 * Компонент предоставляет функциональность выпадающего списка с поддержкой управления состоянием, кастомизации и обработки событий.
 *
 * @param {Object} props - Свойства компонента.
 * @param {Array} props.options - Массив опций для отображения в выпадающем списке. Если не передан, опции извлекаются из дочерних элементов.
 *
 * Структура options:
 * - {Object} option:
 *   - key - обязательно, чтобы использовать в списках
 *   - value: any — Значение опции.
 *   - content: React.ReactNode — Контент, отображаемый для опции.
 *   - disabled: boolean — Отключение опции.
 *   - search: string — Текст для поиска (опционально).
 *   - children: string — Текст дочернего элемента (опционально).
 *   - text: string — Альтернативный текст для отображения (опционально).
 *   - label: string — Метка группы (если это группа).
 *   - options: Array — Список вложенных опций (если это группа).
 *   - data: any — Дополнительная информация для группы (если это группа).
 *
 * Примеры структуры options:
 *
 * Одноуровневый список:
 * [
 *   { key: '1', value: '1', content: 'Опция 1', disabled: false },
 *   { key: '2', value: '2', content: 'Опция 2', disabled: true, text: 'Текст опции' },
 *   { key: '3', value: '3', content: 'Опция 3', search: 'Поиск опции', children: 'Дочерний текст' }
 * ]
 *
 * Группированный список:
 * [
 *   { label: 'Группа 1', data: { info: 'Дополнительная информация' }, options: [
 *       { key: '1', value: '1', content: 'Опция 1' },
 *       { key: '2', value: '2', content: 'Опция 2', text: 'Текст опции' }
 *     ]
 *   },
 *   { label: 'Группа 2', options: [
 *       { key: '3', value: '3', content: 'Опция 3', search: 'Поиск опции' },
 *       { key: '4', value: '4', content: 'Опция 4', disabled: true, children: 'Дочерний текст' }
 *     ]
 *   }
 * ]
 *
 * @param {React.ReactNode} props.children - Дочерние элементы, которые могут быть использованы для определения опций.
 * @param {any} props.value - Текущее значение, выбранное в выпадающем списке.
 * @param {Function} props.onChange - Функция-обработчик, вызываемая при изменении значения.
 * @param {Function} props.createSearch - Функция для создания поискового функционала.
 * @param {Object} props.otherProps - Дополнительные свойства, передаваемые в компонент.
 *
 * otherProps может включать:
 * - disabled: boolean — Отключение выпадающего списка.
 * - loading: boolean — Состояние загрузки.
 * - multiple: boolean — Возможность выбора нескольких опций.
 * - label: string — Метка для выпадающего списка.
 * - placeholder: string — Текст-заполнитель для выпадающего списка.
 * - renderOption: Function — Кастомная функция рендера для опций.
 * - renderOptionGroup: Function — Кастомная функция рендера для групп опций.
 * - renderControl: Function — Кастомная функция рендера для элемента управления.
 * - renderActions: Function — Кастомная функция рендера для действий.
 * - renderSelectedOption: Function — Кастомная функция рендера для выбранной опции.
 *
 * @returns {React.Element} React-элемент.
 */

const { selectValue } = selectors;
const visibleSelector = (state) => selectValue(state, stateKeys.visible);

const Dropdown = ({ children, overlay }) => {
  const visible = useStoreSelector(visibleSelector);
  const { onVisibleChange } = useHandlers();

  return (
    <AntDropdown
      trigger={['click']}
      visible={visible}
      onVisibleChange={onVisibleChange}
      overlay={overlay}
      getPopupContainer={(triggerNode) => triggerNode.parentNode}
    >
      {children}
    </AntDropdown>
  );
};

const Content = memo(() => {
  return (
    <Dropdown overlay={<OptionsContainer />}>
      <Control />
    </Dropdown>
  );
});

const DropdownSelect = forwardRef((props, ref) => {
  const { options, children, value, onChange, createSearch, ...otherProps } =
    props;

  // Ставим все,
  // что используется в handlers и не используется в других компонентах
  const valueRef = useValueRef({ value, onChange, createSearch });

  const store = useMemo(() => new ObservableStore(getInitialState()), []);

  useEffect(() => {
    const state = store.getState();
    for (const key of updatedProps) {
      if (!Object.is(state[key], otherProps[key])) {
        store.setProps(otherProps);
        break;
      }
    }
  }, [otherProps, store]);

  const rawOptions = useMemo(() => {
    return options || getOptionsFromChildren(children);
  }, [options, children]);

  const optionsData = useMemo(
    () => getFlattenOptionsAndCount(rawOptions),
    [rawOptions]
  );

  useEffect(() => {
    if (optionsData) {
      const search = store.getState()[stateKeys.search];

      const filteredOptions = filterOptions({
        options: optionsData.options,
        search,
        createSearch: valueRef.current.createSearch,
        value: valueRef.current.value,
      });

      store.setProps({
        ...optionsData,
        [stateKeys.filteredOptions]: filteredOptions,
      });
    }
  }, [optionsData, store, valueRef]);

  useEffect(() => {
    const key = stateKeys.currentValue;
    const { getState, setProps } = store;

    if (value !== getState()[key]) {
      setProps({ [key]: value });
    }
  }, [value, store]);

  const handlers = useCreateHandlers({ store, valueRef });

  return (
    <StoreContext.Provider value={store}>
      <ValueContext.Provider value={value}>
        <HandlersContext.Provider value={handlers}>
          <Content />
        </HandlersContext.Provider>
      </ValueContext.Provider>
    </StoreContext.Provider>
  );
});

const Option = (_props) => null;
const OptionGroup = (_props) => null;

DropdownSelect.Option = Option;
DropdownSelect.OptionGroup = OptionGroup;

export default DropdownSelect;
